import React, { useEffect, useRef, useState } from "react";
import { List } from "immutable";
import { Alert, Box, Button, Input, Text } from "@showitinc/showit-wc";

import AppConstants from "../../../../constants/AppConstants";
import DocumentStore from "../../../../document/DocumentStore";
import { COMMANDS } from "../../../../document/commands";
import BlockUpdateVO from "../../../../vo/BlockUpdate.mjs";
import styles from "./AdvancedProperties.module.css";
import { BLOCK } from "@/constants/DataTypes";

/**
 * @typedef {import("immutable").Map} Entity - Block or element with selection info
 */

const { INPUT_SOURCE_PROPERTY_INPUT, UPDATE_ACTION_SET, UPDATE_ACTION_DELETE } =
  AppConstants;
/** @type {"htmlDataAttributes"} */
const HTML_DATA_ATTRS = "htmlDataAttributes";
/** @type {"htmlClasses"} */
const HTML_CLASSES = "htmlClasses";
/** @type {"elementData"} */
const ELEMENT_DATA = "elementData";
/** @type {"elementClass"} */
const ELEMENT_CLASS = "elementClass";
const VALID_ATTR_NAME_REGEX = /^[a-zA-Z0-9-_]+$/;
const VALID_ATTR_VALUE_REGEX = /^[a-zA-Z0-9\-_. ]+$/;
const RESERVED_PROP_NAMES = [
  "class",
  "id",
  "style",
  "data",
  "aria",
  "role",
  "tabindex",
  "script",
];

/**
 * Gets id based on type
 * @param {Entity} entity
 * @returns
 */
const getId = (entity) =>
  entity?.get("type") === BLOCK
    ? entity?.get("id")
    : entity.getIn(["selected", "id"]);

/**
 *
 * @param {Object} props
 * @param {Entity} props.entity
 * @param {boolean} [props.isLocked]
 * @returns {React.ReactElement}
 */
const CustomElementData = ({ entity, isLocked }) => {
  const [classOrKey, setClassOrKey] = useState("");
  const [dataValue, setDataValue] = useState("");
  const [error, setError] = useState("");
  const [editing, setEditing] = useState("");
  const classOrKeyInput = useRef(null);

  useEffect(() => {
    if (editing && classOrKeyInput) {
      classOrKeyInput?.current?.focus?.();
      setError("");
    }
  }, [editing]);

  const currentClasses = entity.get(HTML_CLASSES, new List());
  const currentDataAttrs = entity.get(HTML_DATA_ATTRS, new List());
  const hasDataAttrs = currentDataAttrs.size > 0;
  const hasClasses = currentClasses.size > 0;

  const isClass = editing === ELEMENT_CLASS;

  const _onAddElementAttribute = () => {
    const classOrKeyLabel = isClass ? "class" : "key";
    if (
      classOrKey.trim() === "" ||
      !classOrKey.trim().match(VALID_ATTR_NAME_REGEX) ||
      RESERVED_PROP_NAMES.includes(classOrKey.toLowerCase().trim())
    ) {
      setError(`Invalid ${classOrKeyLabel} name`);
      return;
    }

    if (
      !isClass &&
      (dataValue.trim() === "" ||
        !dataValue.trim().match(VALID_ATTR_VALUE_REGEX))
    ) {
      setError("Invalid value");
      return;
    }

    const formattedClassOrKey = classOrKey
      .trim()
      .replace(/\s+/g, "-")
      .replace(/"/g, "&quot;");

    const newData = isClass
      ? formattedClassOrKey
      : [formattedClassOrKey, dataValue.trim().replace(/"/g, "&quot;")];

    let mergedData;
    if (isClass) {
      const existingClass = currentClasses.findIndex(
        (name) => name === newData,
      );
      if (existingClass > -1) {
        setError("Duplicate class");
        return;
      }
      mergedData = [...currentClasses, newData];
    } else {
      // check for existing key in currentDataAttrs
      const existingKey = currentDataAttrs.findIndex(
        ([name]) => name === newData[0],
      );
      if (existingKey > -1) {
        setError("Duplicate key");
        return;
      }
      mergedData = [...currentDataAttrs, newData];
    }
    const newAttributeUpdate = {
      name: isClass ? HTML_CLASSES : HTML_DATA_ATTRS,
      value: mergedData,
      action: UPDATE_ACTION_SET,
      source: INPUT_SOURCE_PROPERTY_INPUT,
    };

    const id = getId(entity);
    const update = new BlockUpdateVO();
    update.addPropUpdate(id, newAttributeUpdate);
    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
    _resetForm();
  };

  const _resetForm = () => {
    setClassOrKey("");
    setDataValue("");
    setError("");
    setEditing("");
  };

  /**
   * 
   * @param {number} index 
   * @param {typeof ELEMENT_CLASS | typeof ELEMENT_DATA} type 
   */
  const _onRemoveElementData = (index, type) => {
    const newAttributeList = [
      ...(type === ELEMENT_CLASS ? currentClasses : currentDataAttrs),
    ];
    newAttributeList.splice(index, 1);

    const newAttributeUpdate = {
      name: type === ELEMENT_CLASS ? HTML_CLASSES : HTML_DATA_ATTRS,
      value: newAttributeList,
      action:
        newAttributeList.length > 0 ? UPDATE_ACTION_SET : UPDATE_ACTION_DELETE,
      source: INPUT_SOURCE_PROPERTY_INPUT,
    };

    const id = getId(entity);
    const update = new BlockUpdateVO();
    update.addPropUpdate(id, newAttributeUpdate);
    DocumentStore.dispatch({ type: COMMANDS.UPDATE_BLOCK, payload: update });
  };

  /**
   * @param {CustomEvent<{ key: string }>} e
   */
  const _handleKeyDown = (e) => {
    if (e.detail?.key === "Escape") {
      _resetForm();
    }
  };

  return (
    <>
      <Box my="2">
        <Box d="flex" justify="between" align="center" mb="1" pl="3" pr="1">
          <Text variant="body-strong">Class Selectors</Text>
          <Button
            data-testid="class-selector-add-button"
            iconPrefix="add"
            variant="icon"
            iconSize="20"
            onClick={() => setEditing(ELEMENT_CLASS)}
            disabled={isLocked}
          />
        </Box>
        {hasClasses &&
          currentClasses.map((className, i) => (
            <Box
              className={styles.customAttribute}
              d="flex"
              align="center"
              pl="3"
              pr="1"
              justify="between"
              key={className}
            >
              <Text className={styles.customAttributeText}>.{className}</Text>
              <Button
                onClick={() => _onRemoveElementData(i, ELEMENT_CLASS)}
                variant="icon"
                iconPrefix="close"
                disabled={isLocked}
              />
            </Box>
          ))}
        <Box>
          {editing === ELEMENT_CLASS && (
            <>
              <Input
                data-testid="class-selector-input"
                ref={classOrKeyInput}
                className={styles.customAttributeInput}
                onSISubmit={_onAddElementAttribute}
                label="Class"
                placeholder="Enter class here"
                value={classOrKey}
                onFocus={() => setError("")}
                onSIInput={(e) =>
                  setClassOrKey(e.target.value.replace(".", ""))
                }
                onSIKeydown={_handleKeyDown}
                disabled={isLocked}
              >
                <Button
                  slot="suffix"
                  iconPrefix="check"
                  variant="icon"
                  disabled={classOrKey.trim() === ""}
                  onClick={_onAddElementAttribute}
                />
              </Input>
              {error && (
                <Box my="2">
                  <Alert
                    weight="bold"
                    variant="error"
                    message={`Error: ${error}`}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
      {/* This works, but is not wanted. my heart can't let go, so it's commented out. */}
      {/* <Box my="2">
        <Box pl="3" pb="1" className={styles.customAttributeHeading}>
          <Text>Data Attributes</Text>
        </Box>
        {hasDataAttrs &&
          currentDataAttrs.map(([key, value], i) => (
            <Box
              className={styles.customAttribute}
              d="flex"
              align="center"
              pl="3"
              pr="1"
              justify="between"
              key={key}
            >
              <Text className={styles.customAttributeText}>
                {key}: {value}
              </Text>
              <Button
                onClick={() => _onRemoveElementData(i, ELEMENT_DATA)}
                variant="icon"
                iconPrefix="remove"
              />
            </Box>
          ))}
        <Box>
          {editing === ELEMENT_DATA ? (
            <>
              <Input
                ref={classOrKeyInput}
                label="Key"
                placeholder="Enter data here"
                value={classOrKey}
                onFocus={() => setError("")}
                onSIInput={(e) => setClassOrKey(e.target.value)}
              />
              <Input
                label="Value"
                onSISubmit={_onAddElementAttribute}
                placeholder="Enter value here"
                value={dataValue}
                onFocus={() => setError("")}
                onSIInput={(e) => setDataValue(e.target.value)}
              >
                <Button
                  slot="suffix"
                  iconPrefix="add"
                  variant="icon"
                  disabled={classOrKey.trim() === ""}
                  onClick={_onAddElementAttribute}
                />{" "}
              </Input>
              {error && (
                <Box my="2">
                  <Alert weight="bold" variant="error" message={`Error: ${error}`} />
                </Box>
              )}
            </>
          ) : (
            <Button
              className={styles.addButton}
              iconPrefix="add"
              variant="minimal"
              fullWidth
              onClick={() => setEditing(ELEMENT_DATA)}
            >
              Add Data Attribute
            </Button>
          )}
        </Box>
      </Box> */}
    </>
  );
};

export default CustomElementData;
